import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LanguageSelector } from '../components/LanguageSelector';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Eye, EyeOff, Shield, Zap, Globe, CreditCard, ShieldCheck, Sun, Moon } from 'lucide-react';

const LOGO_URL = "/logo-alphabit.png";

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const { login } = useAuth();
  const { t } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password, requires2FA ? totpCode : null);
      toast.success(t('auth.loginSuccess'));
      
      // Check if must change password
      if (user.must_change_password) {
        window.location.href = '/change-password?first=true';
        return;
      }
      
      // Redirect based on role
      if (user.role === 'super_admin') {
        window.location.href = '/admin';
      } else if (user.role === 'agent') {
        window.location.href = '/agent';
      } else {
        window.location.href = '/dashboard';
      }
    } catch (error) {
      const status = error.response?.status;
      const isServiceUnavailable = !error.response || (status && status >= 500);

      if (isServiceUnavailable) {
        toast.error(t('auth.serviceUnavailable'));
        setLoading(false);
        return;
      }

      // Check if 2FA is required
      if (status === 428 && error.response?.data?.detail === '2FA_REQUIRED') {
        setRequires2FA(true);
        setTotpCode('');
        toast.info('Inserisci il codice 2FA dalla tua app');
        setLoading(false);
        return;
      }
      toast.error(error.response?.data?.detail || t('auth.invalidCredentials'));
      setLoading(false);
    }
  };

  const features = [
    { icon: Shield, label: 'Sicurezza Avanzata', desc: 'Transazioni protette 24/7' },
    { icon: Zap, label: 'Pagamenti Istantanei', desc: 'Elaborazione in tempo reale' },
    { icon: Globe, label: 'Multi-Valuta', desc: 'EUR, USD, GBP supportati' },
    { icon: CreditCard, label: 'Multi-Gateway', desc: 'PayPal, Stripe e altri' },
  ];

  return (
    <div className="min-h-screen flex">
      {/* Left side - Hero/Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Gradient Background */}
        <div className={`absolute inset-0 ${
          isDark 
            ? 'bg-gradient-to-br from-[#12002F] via-[#2A005F] to-[#0B0F2A]' 
            : 'bg-gradient-to-br from-violet-600 via-purple-600 to-fuchsia-500'
        }`} />
        
        {/* Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden">
          {isDark ? (
            <>
              <div className="absolute -top-24 -left-24 w-96 h-96 bg-purple-500/20 rounded-full blur-[150px]" />
              <div className="absolute top-1/2 -right-24 w-80 h-80 bg-[#9D4DFF]/30 rounded-full blur-[150px]" />
              <div className="absolute -bottom-24 left-1/3 w-72 h-72 bg-[#6A00FF]/30 rounded-full blur-[150px]" />
            </>
          ) : (
            <>
              <div className="absolute -top-24 -left-24 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
              <div className="absolute top-1/2 -right-24 w-80 h-80 bg-fuchsia-400/20 rounded-full blur-3xl" />
              <div className="absolute -bottom-24 left-1/3 w-72 h-72 bg-violet-400/20 rounded-full blur-3xl" />
            </>
          )}
          
          {/* Grid Pattern */}
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center p-12 xl:p-16">
          {/* Logo */}
          <div className="flex items-center gap-4 mb-12">
            <div className={`w-16 h-16 backdrop-blur-sm rounded-2xl p-2 shadow-2xl ${
              isDark ? 'bg-white/10 border border-purple-500/30' : 'bg-white/20'
            }`}>
              <img src={LOGO_URL} alt="AlphaBit" className="w-full h-full object-contain" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Alpha<span className={isDark ? 'text-[#9D4DFF]' : 'text-fuchsia-200'}>Bit</span></h1>
              <p className="text-white/70 text-sm">Payment Gateway</p>
            </div>
          </div>
          
          {/* Main Text */}
          <div className="mb-12">
            <h2 className="text-4xl xl:text-5xl font-bold text-white mb-6 leading-tight">
              La tua piattaforma<br />
              <span className={`text-transparent bg-clip-text ${
                isDark 
                  ? 'bg-gradient-to-r from-[#9D4DFF] to-white' 
                  : 'bg-gradient-to-r from-fuchsia-200 to-white'
              }`}>
                di pagamento
              </span>
            </h2>
            <p className="text-white/80 text-lg max-w-md">
              Gestisci i tuoi pagamenti in modo semplice, sicuro e professionale con AlphaBit.
            </p>
          </div>
          
          {/* Features Grid */}
          <div className="grid grid-cols-2 gap-4">
            {features.map((feature, index) => (
              <div 
                key={index}
                className={`backdrop-blur-sm rounded-xl p-4 border transition-all duration-300 ${
                  isDark 
                    ? 'bg-white/5 border-purple-500/30 hover:bg-white/10' 
                    : 'bg-white/10 border-white/20 hover:bg-white/15'
                }`}
              >
                <feature.icon className={`w-8 h-8 mb-3 ${isDark ? 'text-[#9D4DFF]' : 'text-fuchsia-200'}`} />
                <h3 className="text-white font-semibold mb-1">{feature.label}</h3>
                <p className="text-white/60 text-sm">{feature.desc}</p>
              </div>
            ))}
          </div>
          
          {/* Stats */}
          <div className={`flex gap-8 mt-12 pt-8 border-t ${isDark ? 'border-purple-500/30' : 'border-white/20'}`}>
            <div>
              <p className="text-3xl font-bold text-white">99.9%</p>
              <p className="text-white/60 text-sm">Uptime</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-white">&lt;1s</p>
              <p className="text-white/60 text-sm">Latenza</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-white">24/7</p>
              <p className="text-white/60 text-sm">Supporto</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className={`flex-1 lg:w-1/2 flex items-center justify-center p-6 sm:p-8 lg:p-12 ${
        isDark 
          ? 'bg-[#0B0F2A]' 
          : 'bg-gradient-to-br from-slate-50 to-slate-100'
      }`}>
        <div className="w-full max-w-md">
          {/* Mobile Header */}
          <div className="flex items-center justify-between mb-8">
            <Link to="/" className={`inline-flex items-center gap-2 transition-colors font-medium ${
              isDark ? 'text-gray-400 hover:text-[#9D4DFF]' : 'text-slate-600 hover:text-violet-600'
            }`}>
              <ArrowLeft className="w-4 h-4" />
              {t('auth.backToHome')}
            </Link>
            <div className="flex items-center gap-2">
              {/* Theme Toggle */}
              <button 
                onClick={toggleTheme}
                className={`w-8 h-8 flex items-center justify-center rounded-full transition-all hover:scale-110 ${
                  isDark 
                    ? 'bg-purple-500/20 text-yellow-400 hover:bg-purple-500/30' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid="theme-toggle"
              >
                {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
              <LanguageSelector />
            </div>
          </div>
          
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src={LOGO_URL} alt="AlphaBit" className="w-12 h-12 object-contain" />
            <span className={`font-bold text-2xl ${isDark ? 'text-white' : 'text-slate-900'}`}>Alpha<span className={isDark ? 'text-[#9D4DFF]' : 'text-violet-600'}>Bit</span></span>
          </div>

          {/* Form Card */}
          <div className={`rounded-2xl shadow-xl p-8 border ${
            isDark 
              ? 'bg-[#0F0F2F] border-purple-500/30 shadow-purple-500/10' 
              : 'bg-white border-slate-100 shadow-slate-200/50'
          }`}>
            <div className="text-center mb-8">
              <h1 className={`text-2xl sm:text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {t('auth.welcomeBack')}
              </h1>
              <p className={isDark ? 'text-gray-400' : 'text-slate-500'}>
                {t('auth.loginSubtitle')}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className={`font-medium ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>{t('common.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="nome@azienda.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={requires2FA}
                  data-testid="login-email"
                  className={`h-12 rounded-xl ${
                    isDark 
                      ? 'bg-white/5 border-purple-500/30 text-white placeholder:text-gray-500 focus:border-[#9D4DFF] focus:ring-[#9D4DFF]/20' 
                      : 'bg-slate-50 border-slate-200 focus:border-violet-500 focus:ring-violet-500/20'
                  }`}
                />
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className={`font-medium ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>{t('common.password')}</Label>
                  <Link to="/forgot-password" className={`text-sm font-medium ${isDark ? 'text-[#9D4DFF] hover:text-[#B76EFF]' : 'text-violet-600 hover:text-violet-700'}`}>
                    {t('auth.forgotPassword')}
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={requires2FA}
                    data-testid="login-password"
                    className={`h-12 rounded-xl pr-12 ${
                      isDark 
                        ? 'bg-white/5 border-purple-500/30 text-white placeholder:text-gray-500 focus:border-[#9D4DFF] focus:ring-[#9D4DFF]/20' 
                        : 'bg-slate-50 border-slate-200 focus:border-violet-500 focus:ring-violet-500/20'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute right-4 top-1/2 -translate-y-1/2 transition-colors ${
                      isDark ? 'text-gray-500 hover:text-[#9D4DFF]' : 'text-slate-400 hover:text-violet-600'
                    }`}
                    data-testid="toggle-password"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* 2FA Code Input */}
              {requires2FA && (
                <div className={`space-y-2 p-4 rounded-xl ${
                  isDark 
                    ? 'bg-amber-500/10 border border-amber-500/30' 
                    : 'bg-amber-50 border border-amber-200'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    <ShieldCheck className={`w-5 h-5 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
                    <Label className={`font-semibold ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>Codice 2FA Richiesto</Label>
                  </div>
                  <p className={`text-sm mb-3 ${isDark ? 'text-amber-400/80' : 'text-amber-700'}`}>
                    Inserisci il codice a 6 cifre dalla tua app di autenticazione
                  </p>
                  <Input
                    type="text"
                    maxLength={6}
                    placeholder="000000"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                    autoFocus
                    data-testid="login-2fa-code"
                    className={`h-14 text-center text-2xl font-mono tracking-widest rounded-xl ${
                      isDark 
                        ? 'bg-white/10 border-amber-500/50 text-white focus:border-amber-400 focus:ring-amber-400/20' 
                        : 'bg-white border-amber-300 focus:border-amber-500 focus:ring-amber-500/20'
                    }`}
                  />
                  <button 
                    type="button"
                    onClick={() => { setRequires2FA(false); setTotpCode(''); }}
                    className={`text-sm mt-2 ${isDark ? 'text-amber-400 hover:text-amber-300' : 'text-amber-600 hover:text-amber-700'}`}
                  >
                    ← Torna indietro
                  </button>
                </div>
              )}
              
              <Button 
                type="submit" 
                className={`w-full h-12 font-semibold rounded-xl shadow-lg transition-all duration-300 ${
                  isDark 
                    ? 'bg-gradient-to-r from-[#6A00FF] to-[#9D4DFF] hover:from-[#7B1FFF] hover:to-[#AE6EFF] shadow-purple-500/30' 
                    : 'bg-gradient-to-r from-violet-600 to-fuchsia-500 hover:from-violet-700 hover:to-fuchsia-600 shadow-violet-500/30'
                }`} 
                disabled={loading || (requires2FA && totpCode.length !== 6)}
                data-testid="login-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    {t('auth.loggingIn')}
                  </>
                ) : requires2FA ? (
                  'Verifica e Accedi'
                ) : (
                  t('auth.loginButton')
                )}
              </Button>
            </form>

            <div className={`mt-8 pt-6 border-t text-center ${isDark ? 'border-purple-500/20' : 'border-slate-100'}`}>
              <p className={isDark ? 'text-gray-400' : 'text-slate-600'}>
                {t('auth.noAccount')}{' '}
                <Link to="/register" className={`font-semibold ${isDark ? 'text-[#9D4DFF] hover:text-[#B76EFF]' : 'text-violet-600 hover:text-violet-700'}`}>
                  {t('common.register')}
                </Link>
              </p>
            </div>
          </div>
          
          {/* Trust Badge */}
          <div className="mt-6 text-center">
            <p className={`text-sm flex items-center justify-center gap-2 ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
              <Shield className="w-4 h-4" />
              Connessione sicura e crittografata
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
